import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { getApiErrorMessage } from '@/api/client';
import { useSeo } from '@/hooks/useSeo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** ?next= — faqat sayt ichidagi yo'l (open redirect'ga yo'l qo'ymaslik uchun "//" va "/\" rad etiladi) */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/profile';
  return raw;
}

export function Login() {
  useSeo({ title: "Kirish — SQLMaster.uz", path: '/login', noindex: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const push = useToastStore((s) => s.push);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!EMAIL_RE.test(email)) next.email = t('auth.errorEmail');
    if (password.length < 8) next.password = t('auth.errorPasswordShort');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    try {
      await login(email, password);
      push(t('toast.loggedIn'), 'success');
      navigate(safeNext(searchParams.get('next')), { replace: true });
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>

      <div className="card p-6 sm:p-8">
        <h1 className="mb-1 text-2xl font-bold">{t('auth.loginTitle')}</h1>
        <p className="mb-6 text-sm text-content-secondary">{t('auth.loginSubtitle')}</p>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="input pl-10"
                autoComplete="email"
              />
            </div>
            {errors.email && <p className="mt-1 text-xs text-[var(--error)]">{errors.email}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                className="input pl-10 pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content"
                aria-label={t('auth.password')}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-[var(--error)]">{errors.password}</p>}
            <div className="mt-1.5 text-right">
              <Link to="/forgot-password" className="text-xs font-medium text-brand-600 hover:underline">
                {t('auth.forgotLink')}
              </Link>
            </div>
          </div>

          <Button type="submit" loading={loading} className="w-full">
            {t('auth.loginButton')}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-content-secondary">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="font-medium text-brand-600 hover:underline">
            {t('auth.signupLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
